import ApiWithLogging from '../utils/apiWithLogging';

export interface DisplaySettings {
  language: string;
  itemsPerPage: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
}

export interface NotificationSettings {
  enableContractExpiration: boolean;
  contractExpirationDays: number[];
  enableSimExpiration: boolean;
  simExpirationDays: number[];
  enableRentalReminder: boolean;
  rentalReminderDays: number[];
  enablePaymentReminder: boolean;
  paymentReminderDays: number[];
  enablePaymentOverdue: boolean;
  paymentOverdueDays: number[];
  notificationChannels: {
    line: boolean;
    email: boolean;
    inApp: boolean;
    telegram: boolean;
  };
}

export interface SystemSettings {
  autoLogout: string;
  enableAutoBackup: boolean;
  backupFrequency: string;
  backupTime: string;
  logRetentionDays: string;
}

export interface DefaultSettings {
  rentalIdFormat: string;
  contractIdFormat: string;
  defaultRentalDuration: string;
  defaultRentalStatus: string;
}

export interface GeneralSettings {
  displaySettings: DisplaySettings;
  notificationSettings: NotificationSettings;
  systemSettings: SystemSettings;
  defaultSettings: DefaultSettings;
}

class SettingsService {
  // Get all settings
  async getGeneralSettings(): Promise<GeneralSettings> {
    try {
      const data = await ApiWithLogging.get<GeneralSettings>('/settings/general');
      return data;
    } catch (error) {
      console.error('Error fetching general settings:', error);
      throw error;
    }
  }

  // Save display settings
  async saveDisplaySettings(settings: DisplaySettings): Promise<DisplaySettings> {
    try {
      const data = await ApiWithLogging.put<DisplaySettings>('/settings/general/display', settings);
      return data;
    } catch (error) {
      console.error('Error saving display settings:', error);
      throw error;
    }
  }

  // Save notification settings
  async saveNotificationSettings(settings: NotificationSettings): Promise<NotificationSettings> {
    try {
      const data = await ApiWithLogging.put<NotificationSettings>('/settings/general/notification', settings);
      return data;
    } catch (error) {
      console.error('Error saving notification settings:', error);
      throw error;
    }
  }

  // Save system settings
  async saveSystemSettings(settings: SystemSettings): Promise<SystemSettings> {
    try {
      const data = await ApiWithLogging.put<SystemSettings>('/settings/general/system', settings);
      return data;
    } catch (error) {
      console.error('Error saving system settings:', error);
      throw error;
    }
  }

  // Save default settings
  async saveDefaultSettings(settings: DefaultSettings): Promise<DefaultSettings> {
    try {
      const data = await ApiWithLogging.put<DefaultSettings>('/settings/general/defaults', settings);
      return data;
    } catch (error) {
      console.error('Error saving default settings:', error);
      throw error;
    }
  }
}

export default new SettingsService();